import { patchHasInternalLeak, textHasDraftMarkers } from './editorialLeak';

export interface BlogImportPatch {
    slug: string;
    title: string;
    excerpt?: string;
    body_html: string;
    meta_title?: string;
    meta_description?: string;
    main_image_url?: string;
    og_image_url?: string;
    type_slug?: string;
    main_image_position?: 'top' | 'side';
    /** Si el fichero pide enabled sin imagen, se fuerza a draft en finalize. */
    status?: 'draft' | 'review' | 'enabled';
}

export interface ParsedBlogFile {
    filename: string;
    slug: string;
    patch: BlogImportPatch;
    warnings: string[];
    error?: string;
    leak: boolean;
}

export interface BlogBatchImportItem extends BlogImportPatch {
    filename?: string;
}

const FRONTMATTER_DASH = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;
const FRONTMATTER_HTML = /^<!--([\s\S]*?)-->/;

function unquote(value: string): string {
    const v = value.trim();
    if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
        return v.slice(1, -1);
    }
    return v;
}

function parseFrontmatterBlock(block: string): Record<string, string> {
    const attrs: Record<string, string> = {};
    for (const line of block.split(/\r?\n/)) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) continue;
        const idx = trimmed.indexOf(':');
        if (idx < 1) continue;
        const key = trimmed.slice(0, idx).trim().toLowerCase().replace(/-/g, '_');
        attrs[key] = unquote(trimmed.slice(idx + 1));
    }
    return attrs;
}

function splitFrontmatter(text: string): { attrs: Record<string, string>; body: string } {
    const dash = text.match(FRONTMATTER_DASH);
    if (dash) {
        return { attrs: parseFrontmatterBlock(dash[1]), body: text.slice(dash[0].length).trim() };
    }
    const html = text.match(FRONTMATTER_HTML);
    if (html) {
        return { attrs: parseFrontmatterBlock(html[1]), body: text.slice(html[0].length).trim() };
    }
    return { attrs: {}, body: text.trim() };
}

function slugFromFilename(name: string): string {
    return name
        .replace(/\.[^.]+$/, '')
        .replace(/^\d+-/, '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}

function firstImgSrc(html: string): string {
    const m = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    return m?.[1]?.trim() || '';
}

function firstHeading(html: string): string {
    const m = html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/i);
    if (!m) return '';
    return m[1].replace(/<[^>]+>/g, '').trim();
}

function looksLikeHtml(text: string): boolean {
    return /<[a-z][\s\S]*?>/i.test(text);
}

function inlineMd(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" />')
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+)\*/g, '<em>$1</em>');
}

/** Conversión mínima MD → HTML. Si ya hay tags, se deja tal cual. */
export function markdownToHtml(source: string): string {
    const text = source.trim();
    if (!text) return '';
    if (looksLikeHtml(text)) return text;

    const lines = text.split(/\r?\n/);
    const out: string[] = [];
    let listType: 'ul' | 'ol' | null = null;
    let para: string[] = [];

    const flushPara = () => {
        if (para.length === 0) return;
        out.push(`<p>${inlineMd(para.join(' '))}</p>`);
        para = [];
    };
    const flushList = () => {
        if (!listType) return;
        out.push(`</${listType}>`);
        listType = null;
    };

    for (const raw of lines) {
        const line = raw.trimEnd();
        const heading = line.match(/^(#{1,3})\s+(.+)$/);
        const ul = line.match(/^[-*]\s+(.+)$/);
        const ol = line.match(/^\d+\.\s+(.+)$/);
        if (heading) {
            flushPara();
            flushList();
            out.push(`<h${heading[1].length}>${inlineMd(heading[2])}</h${heading[1].length}>`);
            continue;
        }
        if (ul) {
            flushPara();
            if (listType !== 'ul') {
                flushList();
                listType = 'ul';
                out.push('<ul>');
            }
            out.push(`<li>${inlineMd(ul[1])}</li>`);
            continue;
        }
        if (ol) {
            flushPara();
            if (listType !== 'ol') {
                flushList();
                listType = 'ol';
                out.push('<ol>');
            }
            out.push(`<li>${inlineMd(ol[1])}</li>`);
            continue;
        }
        if (!line.trim()) {
            flushPara();
            flushList();
            continue;
        }
        if (listType) flushList();
        para.push(line.trim());
    }
    flushPara();
    flushList();
    return out.join('\n');
}

function patchFromAttrs(attrs: Record<string, string>, bodyHtml: string): BlogImportPatch {
    const slug = (attrs.slug || attrs.seo_slug || '').trim();
    const title = (attrs.title || attrs.nombre || firstHeading(bodyHtml) || '').trim();
    const excerpt = (attrs.excerpt || attrs.resumen || '').trim();
    const metaTitle = (attrs.meta_title || attrs.seo_title || '').trim();
    const metaDesc = (attrs.meta_description || attrs.seo_description || '').trim();
    const image = (
        attrs.main_image_url || attrs.image || attrs.hero || attrs.hero_image || firstImgSrc(bodyHtml) || ''
    ).trim();
    const og = (attrs.og_image_url || image).trim();
    const typeSlug = (attrs.type_slug || attrs.type || '').trim();
    const position = attrs.main_image_position === 'side' ? 'side' : 'top';
    const rawStatus = (attrs.status || '').trim().toLowerCase();
    const status =
        rawStatus === 'enabled' || rawStatus === 'draft' || rawStatus === 'review'
            ? rawStatus
            : undefined;
    return {
        slug,
        title,
        body_html: bodyHtml,
        ...(excerpt ? { excerpt } : {}),
        ...(metaTitle ? { meta_title: metaTitle } : {}),
        ...(metaDesc ? { meta_description: metaDesc } : {}),
        ...(image ? { main_image_url: image } : {}),
        ...(og ? { og_image_url: og } : {}),
        ...(typeSlug ? { type_slug: typeSlug } : {}),
        ...(status ? { status } : {}),
        main_image_position: position,
    };
}

function parseObject(filename: string, raw: Record<string, unknown>): ParsedBlogFile {
    const rec = raw as Record<string, unknown>;
    const nested = rec.patch && typeof rec.patch === 'object' ? rec.patch as Record<string, unknown> : rec;
    const bodyRaw = String(nested.body_html ?? nested.body ?? nested.html ?? nested.content ?? '');
    const bodyHtml = markdownToHtml(bodyRaw);
    const attrs: Record<string, string> = {};
    for (const [k, v] of Object.entries(nested)) {
        if (v == null || typeof v === 'object') continue;
        attrs[k.toLowerCase()] = String(v);
    }
    const patch = patchFromAttrs(attrs, bodyHtml);
    if (!patch.slug && typeof rec.slug === 'string') patch.slug = rec.slug;
    return finalize(filename, patch);
}

function finalize(filename: string, patch: BlogImportPatch): ParsedBlogFile {
    const warnings: string[] = [];
    if (!patch.slug) patch.slug = slugFromFilename(filename);
    if (!patch.title) warnings.push('Sin título');
    if (!patch.body_html.trim()) {
        return {
            filename,
            slug: patch.slug,
            patch,
            warnings,
            leak: false,
            error: 'Falta cuerpo HTML',
        };
    }
    if (!patch.excerpt) warnings.push('Sin excerpt');
    if (!patch.meta_title) warnings.push('Sin meta_title');
    if (!patch.main_image_url) {
        if (patch.status === 'enabled') {
            patch.status = 'draft';
            warnings.push('Importada como borrador: sin imagen principal');
        } else {
            warnings.push('Sin imagen');
        }
    }
    if (textHasDraftMarkers(patch.body_html)) warnings.push('Marcadores de borrador en el HTML');
    const leak = patchHasInternalLeak([
        patch.title, patch.slug, patch.excerpt, patch.body_html, patch.meta_title, patch.meta_description,
    ]);
    return {
        filename,
        slug: patch.slug,
        patch,
        warnings,
        leak,
        error: leak ? 'Notas internas (CHAT/VB/pasada)' : undefined,
    };
}

export function parseBlogPostText(filename: string, text: string): ParsedBlogFile {
    const { attrs, body } = splitFrontmatter(text);
    const bodyHtml = markdownToHtml(body);
    const patch = patchFromAttrs(attrs, bodyHtml);
    return finalize(filename, patch);
}

export function parseBlogPostJsonBatch(filename: string, text: string): ParsedBlogFile[] {
    const parsed = JSON.parse(text) as unknown;
    const rows: unknown[] = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === 'object' && Array.isArray((parsed as { posts?: unknown[] }).posts)
            ? (parsed as { posts: unknown[] }).posts
            : parsed && typeof parsed === 'object' && Array.isArray((parsed as { articles?: unknown[] }).articles)
                ? (parsed as { articles: unknown[] }).articles
                : [parsed];
    return rows.map((row, i) => {
        if (!row || typeof row !== 'object') {
            return {
                filename: `${filename}#${i + 1}`,
                slug: '',
                patch: { slug: '', title: '', body_html: '' },
                warnings: [],
                leak: false,
                error: 'El elemento no es un objeto',
            };
        }
        return parseObject(`${filename}#${i + 1}`, row as Record<string, unknown>);
    });
}

export function parsedToBatchItem(file: ParsedBlogFile): BlogBatchImportItem {
    return { ...file.patch, filename: file.filename };
}
